import { useState } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Switch,
  TimePicker,
  Popconfirm,
  Spin,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Header from "../_shared/components/Header";
import {
  useGetHolidaysQuery,
  useCreateHolidayMutation,
  useDeleteHolidayMutation,
  useGetBusinessHoursQuery,
  useUpdateBusinessHoursMutation,
  useGetFacilitySettingsQuery,
  useUpdateFacilitySettingsMutation,
} from "../../../redux/api/scheduling";
import { setFacilityTimezone } from "../_shared/utils/dateUtils";

function HolidaysSection() {
  const { data, isLoading } = useGetHolidaysQuery({});
  const [createHoliday] = useCreateHolidayMutation();
  const [deleteHoliday] = useDeleteHolidayMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const holidays = data?.results || data || [];

  const handleSubmit = async (values: any) => {
    try {
      await createHoliday({
        ...values,
        date: values.date.format("YYYY-MM-DD"),
      }).unwrap();
      message.success("Holiday added");
      setModalOpen(false);
      form.resetFields();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error adding holiday");
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (v: string) => dayjs(v).format("MM/DD/YYYY"),
    },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this holiday?"
          onConfirm={() => deleteHoliday(record.id)}
        >
          <Button icon={<DeleteOutlined />} size="small" danger />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title="Holidays"
      size="small"
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
        >
          Add Holiday
        </Button>
      }
    >
      <Table
        dataSource={holidays}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        size="small"
        pagination={false}
      />

      <Modal
        title="Add Holiday"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker className="w-full" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Holiday Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

function BusinessHoursSection() {
  const { data, isLoading } = useGetBusinessHoursQuery({});
  const [updateBH] = useUpdateBusinessHoursMutation();

  const hours = data?.results || data || [];

  const handleUpdate = async (id: number, field: string, value: any) => {
    try {
      await updateBH({ id, [field]: value }).unwrap();
      message.success("Business hours updated");
    } catch {
      message.error("Update failed");
    }
  };

  const columns = [
    { title: "Day", dataIndex: "day_label", key: "day_label" },
    {
      title: "Open",
      dataIndex: "is_open",
      key: "is_open",
      render: (v: boolean, r: any) => (
        <Switch
          checked={v}
          onChange={(checked) => handleUpdate(r.id, "is_open", checked)}
        />
      ),
    },
    {
      title: "Open Time",
      dataIndex: "open_time",
      key: "open_time",
      render: (v: string, r: any) => (
        <TimePicker
          value={v ? dayjs(v, "HH:mm:ss") : undefined}
          format="HH:mm"
          disabled={!r.is_open}
          onChange={(t) =>
            t && handleUpdate(r.id, "open_time", t.format("HH:mm:ss"))
          }
        />
      ),
    },
    {
      title: "Close Time",
      dataIndex: "close_time",
      key: "close_time",
      render: (v: string, r: any) => (
        <TimePicker
          value={v ? dayjs(v, "HH:mm:ss") : undefined}
          format="HH:mm"
          disabled={!r.is_open}
          onChange={(t) =>
            t && handleUpdate(r.id, "close_time", t.format("HH:mm:ss"))
          }
        />
      ),
    },
  ];

  return (
    <Card title="Business Hours" size="small" className="mt-4">
      {isLoading ? (
        <Spin size="large" className="mt-4 mx-auto block" />
      ) : (
        <Table
          dataSource={hours}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
        />
      )}
    </Card>
  );
}

const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
];

function FacilityTimezoneSection() {
  const { data, isLoading } = useGetFacilitySettingsQuery({});
  const [updateSettings] = useUpdateFacilitySettingsMutation();

  const handleChange = async (tz: string) => {
    try {
      await updateSettings({ facility_timezone: tz }).unwrap();
      setFacilityTimezone(tz);
      message.success("Facility timezone updated");
    } catch {
      message.error("Failed to update timezone");
    }
  };

  if (isLoading) return <Card title="Facility Timezone" size="small" className="mt-4"><Spin /></Card>;

  return (
    <Card title="Facility Timezone" size="small" className="mt-4">
      <Form layout="inline">
        <Form.Item label="Timezone">
          <Input.Search
            style={{ width: 300 }}
            value={data?.facility_timezone}
            readOnly
            enterButton={false}
          />
        </Form.Item>
      </Form>
      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {COMMON_TIMEZONES.map((tz) => (
          <Button
            key={tz}
            size="small"
            type={data?.facility_timezone === tz ? "primary" : "default"}
            onClick={() => handleChange(tz)}
          >
            {tz.replace(/_/g, " ")}
          </Button>
        ))}
      </div>
    </Card>
  );
}

export default function Calendars() {
  return (
    <div className="w-full p-4">
      <Header title="Calendars" />
      <FacilityTimezoneSection />
      <HolidaysSection />
      <BusinessHoursSection />
    </div>
  );
}
